/* eslint-disable react/prop-types */
import { useMemo } from "react";
import { Alert, Button, Spinner } from "reactstrap";
import dayjs from "dayjs";
import {
  getWorkerDisplayName,
  getWorkerSlug,
  parseWorkerRequestsPage,
  useStep1SuggestedWorkers,
} from "./useStep1SuggestedWorkers";
import SuggestedWorkersStep1View from "./SuggestedWorkersStep1View";

export default function Step1SuggestedWorkers({
  booking,
  bookingSlug,
  refetchBooking,
  onSmartMatchingScheduleKnown,
}) {
  const {
    byShift,
    selected,
    inviteListByShift,
    inviteListSessionError,
    loadMore,
    loadMorePending,
    initialPending,
    toggleDate,
    selectAllDatesForWorker,
    addToInviteList,
    removeFromInviteList,
    rejectWorker,
    inviteWorkers,
    inviteMutation,
    listBusy,
    pendingAppendKey,
    pendingRejectKey,
    pendingRemoveKey,
    invitedQ,
    isFirstInvited,
    shiftInviteMeta,
  } = useStep1SuggestedWorkers({
    booking,
    bookingSlug,
    onAfterInvite: () => refetchBooking?.(),
    onSmartMatchingScheduleKnown,
  });

  const patterns = booking?.shiftPattern ?? [];

  const shiftEntries = useMemo(() => {
    const list = booking?.shiftPattern ?? [];
    return list.map((pattern, i) => ({
      pattern,
      shiftIndex: typeof pattern.shiftIndex === "number" ? pattern.shiftIndex : i,
    }));
  }, [booking?.shiftPattern]);

  if (!patterns.length) {
    return <Alert color="warning">This booking has no shift patterns.</Alert>;
  }

  const invitedItems = invitedQ?.data?.pages?.flatMap((p) => parseWorkerRequestsPage(p).items) || [];

  if (isFirstInvited) {
    return (
      <>
        <Alert color="success" className="mb-3">
          First invites have already been sent for this booking. Suggested workers are now hidden.
        </Alert>

        <h5 className="mb-2">Invited workers</h5>
        <p className="text-muted small mb-3">
          Showing workers already invited in this booking.
        </p>

        {invitedQ.isError && (
          <Alert color="danger" className="py-2">
            Could not load invited workers.
          </Alert>
        )}

        {invitedQ.isLoading && (
          <div className="d-flex align-items-center gap-2 py-3">
            <Spinner size="sm" />
            <span className="text-muted small">Loading invited workers…</span>
          </div>
        )}

        {!invitedQ.isLoading && !invitedQ.isError && !invitedItems.length && (
          <p className="text-muted small mb-0">No invited workers found.</p>
        )}

        {!invitedQ.isLoading &&
          invitedItems.map((req) => (
            <div key={req.id || `${req.workerSlug}-${req.shiftIndex}`} className="border rounded mb-3 p-3 bg-white">
              <div className="fw-semibold">
                {getWorkerDisplayName({
                  ...req?.worker,
                  user: req?.user,
                })}
              </div>
              <div className="small text-muted mb-1">
                {getWorkerSlug({
                  ...req?.worker,
                  user: req?.user,
                  workerSlug: req?.workerSlug,
                }) || "Worker"}
                {" · "}
                Shift {(req?.shiftIndex ?? 0) + 1}
              </div>
              {Array.isArray(req?.workerShift) && req.workerShift.length > 0 && (
                <ul className="list-unstyled small text-muted mb-0">
                  {req.workerShift.map((d) => (
                    <li key={`${req.id || req.workerSlug}-${d.date}`}>
                      {dayjs(d.date).format("MMM D, YYYY")} — {d.status}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

        {!invitedQ.isLoading && invitedQ.hasNextPage && (
          <Button
            color="secondary"
            outline
            size="sm"
            disabled={invitedQ.isFetchingNextPage}
            onClick={() => invitedQ.fetchNextPage()}
          >
            {invitedQ.isFetchingNextPage ? (
              <>
                <Spinner size="sm" className="me-1" /> Loading…
              </>
            ) : (
              "Load more"
            )}
          </Button>
        )}
      </>
    );
  }

  return (
    <SuggestedWorkersStep1View
      shiftEntries={shiftEntries}
      showReject
      byShift={byShift}
      selected={selected}
      inviteListByShift={inviteListByShift}
      inviteListSessionError={inviteListSessionError}
      loadMore={loadMore}
      loadMorePending={loadMorePending}
      initialPending={initialPending}
      toggleDate={toggleDate}
      selectAllDatesForWorker={selectAllDatesForWorker}
      addToInviteList={addToInviteList}
      removeFromInviteList={removeFromInviteList}
      rejectWorker={rejectWorker}
      inviteWorkers={inviteWorkers}
      inviteMutation={inviteMutation}
      listBusy={listBusy}
      pendingAppendKey={pendingAppendKey}
      pendingRejectKey={pendingRejectKey}
      pendingRemoveKey={pendingRemoveKey}
      shiftInviteMeta={shiftInviteMeta}
    />
  );
}
